/**
 * **************************************************************************
 * Workflow features service file
 *
 * Defines all http api calls required in feature workflow page
 * Calls server apis and returns data to components
 * **************************************************************************
 */

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '@app/services/api.service';

@Injectable({
  providedIn: 'root',
})

export class WfFeaturesService {
  /****************** Variable declaration ******************/
  /* define required urls */
  public featureApis = this.apiService.FEATURE_APIS;
  public workflowApis = this.apiService.WORKFLOW_APIS;
  public featureUrl = this.apiService.KPID_URL + this.featureApis.FEATURE;
  public workflowUrl = this.apiService.KPID_URL + this.workflowApis.WORKFLOW;
  
  /**
   * Constructor
   * @param httpClient 
   * @param apiService 
   */
  constructor(
    private httpClient: HttpClient,
    private apiService: ApiService
  ) { }

  /**
   * Retrieve the workflow execution data
   * @param duration - duartion between which data has to be retrieved
   * @param id
   * @returns
   */
  getFeatureWfExeData(duration: String, id: number): Observable<any> {
    const url = this.featureUrl + this.featureApis.FEATURE_PIPELINE_EXEC(duration, id);
    let response = this.httpClient.get(url);
    return response;
  }

  /**
   * Retrieve the details of failed workflow jobs
   * @param duration - duartion between which data has to be retrieved
   * @param id
   * @returns
   */
  getFeatureWfFailData(duration: String, id: number): Observable<any> {
    const url = this.featureUrl + this.featureApis.FEATURE_FAILED_PIPELINE(duration, id);
    let response = this.httpClient.get(url);
    return response;
  }

  /**
   * Retrieve workflow lead time - Not implemented in backend
   * Gives dummy data
   * @param duration - duartion between which data has to be retrieved
   * @param id
   * @returns
   */
  getFeatureWfLeadTime(duration: String, id: number): Observable<any> {
    const url = this.featureUrl + this.featureApis.FEATURE_PIPELINE_LEAD_TIME(duration, id);
    let response = this.httpClient.get(url);
    return response;
  }

  /**
   * Retrieve workflow restore time - Not implemented in backend
   * Gives dummy data
   * @param duration - duartion between which data has to be retrieved
   * @param id
   * @returns
   */
  getFeatureWfRestoreTime(duration: String, id: number): Observable<any> {
    const url =
      this.featureUrl + this.featureApis.FEATURE_TIME_TO_RESTORE_PIPELINE(duration, id);
    let response = this.httpClient.get(url);
    return response;
  }

  /**
   * Retrieve fetaure data
   * @param duration - duartion between which data has to be retrieved
   * @param id
   * @returns
   */
  getFeatureData(duration: String, id: number): Observable<any> {
    const url = this.featureUrl + this.featureApis.FEATURE_DATA(duration, id);
    let response = this.httpClient.get(url, {});
    return response;
  }

  /**
   * Get details of workflow that were executed
   * @param duration - duartion between which data has to be retrieved
   * @param id
   * @returns
   */
  getExecutedWorkflows(duration: String, id: number): Observable<any> {
    const url = this.workflowUrl + this.workflowApis.WORKFLOW_EXECUTED(duration, id);
    let response = this.httpClient.get(url);
    return response;
  }

  /**
   * Retrieve all workflows
   * @returns
   */
  getAllWorkflows(): Observable<any> {
    const url = this.apiService.CONFIG_BUILDER_APIS.WF_HUB + this.apiService.CONFIG_BUILDER_APIS.GET_WORKFLOWS;
    let response = this.httpClient.get(url);
    return response;
  }

  /**
   * Retrieve list of workflows executed and its result
   * @param jobId
   * @returns
   */
  getWfExeRes(jobId: string): Observable<any> {
    const url = this.workflowUrl + this.workflowApis.WORKFLOW_EXE_RES(jobId);
    let response = this.httpClient.get(url);
    return response;
  }

  /**
   * Retrieve quality gate result of a workflow
   * @param jobId - Id of the job whose quality gate result has to be retrieved
   * @returns
   */
  getWfQgRes(jobId: string): Observable<any> {
    const url = this.workflowUrl + this.workflowApis.WORKFLOW_QG_RES(jobId);
    let response = this.httpClient.get(url);
    return response;
  }

  /**
   * Add workflow
   * @param payload
   * @returns
   */
  addWorkflow(activeWf: number | null, payload: any): Observable<any> {
    const url = this.workflowUrl + this.workflowApis.WORKFLOW_ADD(activeWf);
    return this.httpClient.post(url, payload);
  }

  /**
   * Deletes workflow
   * @param id
   * @returns
   */
  deleteWorkflow(id: number): Observable<any> {
    const url = this.workflowUrl + this.workflowApis.WORKFLOW_SOFT_DELETE(id);
    return this.httpClient.delete(url);
  }

  /**
   * Update workflow configuration
   * @param wfId
   * @param payload
   * @returns
   */
  updateWorkflowConfig(wfId: any, disable: number, payload: any): Observable<any> {
    const url = this.workflowUrl + this.workflowApis.WORKFLOW_UPDATE(wfId, disable)
    return this.httpClient.put(url, payload);
  }

  /**
   * Get workflow names
   * @param id
   * @returns
   */
  getWorkflowName(isconfigBuild:boolean=false): Observable<any> {
    const url = this.workflowUrl + this.workflowApis.WORKFLOW_NAME(isconfigBuild)
    return this.httpClient.get(url);
  }
}
